import { Box, Link, Stack, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import Tooltip from './Tooltip';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  actionButton?: ReactNode;
  titleColor?: string;
  titleHint?: ReactNode | string;
  sx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  onClick?: () => void;
  href?: string;
  headerComponentAlign?: 'center' | 'flex-start' | 'flex-end';
  ref?: React.RefObject<HTMLDivElement | null>;
}

const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  icon,
  actionButton,
  titleHint,
  titleColor = 'text.primary',
  sx,
  titleSx,
  onClick,
  href,
  ref,
  headerComponentAlign = 'center',
  ...otherProps
}) => {
  return (
    <Box
      component={href ? Link : 'div'}
      ref={ref}
      href={href}
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: 'none',
        padding: 3,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)'
        } : {},
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx
      }}
      onClick={onClick}
      {...otherProps}
    >
      {(title || icon || actionButton || titleHint) && (
        <Stack direction="row" alignItems={headerComponentAlign} justifyContent="space-between" sx={{ mb: 2, flexShrink: 0 }} spacing={1}>
          <Stack direction="row" alignItems="center" sx={{ width: '100%', overflow: 'hidden' }}>
            {icon && (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {icon}
              </Box>
            )}
            <Stack direction="column" spacing={0.5} sx={{ minWidth: 0, width: '100%' }}>
              {title && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="h6" fontSize={18} fontWeight="600" color={titleColor} sx={titleSx}>
                    {title}
                  </Typography>
                  {titleHint && (
                    <Tooltip text={typeof titleHint === 'string' ? titleHint : String(titleHint)} />
                  )}
                </Stack>
              )}
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
          {actionButton && (
            <Box sx={{ flexShrink: 0 }}>
              {actionButton}
            </Box>
          )}
        </Stack>
      )}
      <Stack spacing={2} width='100%' sx={{ flex: 1, minHeight: 0 }}>
        {children}
      </Stack>
    </Box>
  );
};

export default Card; 